import {ChakraStylesConfig} from 'chakra-react-select';
import {omit} from 'lodash';
import moment from 'moment';
import React, {memo, useEffect, useState} from 'react';
import {HiExclamationCircle, HiOutlineIdentification} from 'react-icons/hi';
import {useDispatch, useSelector} from 'react-redux';
import SchoolsDropdown from '~/components/dropdowns/SchoolsDropdown';
import {
  AppDatePicker,
  Row,
  UiBox,
  UiButton,
  UiCheckbox,
  UiFormControl,
  UiFormErrorMessage,
  UiIcon,
  UiImage,
  UiInput,
  UiRadio,
  UiSimpleGrid,
  UiText,
  UploadResponse,
} from '~/components/elements';
import {Col} from '~/components/elements/grid/Col';
import {UiApplicationsFormsLayout} from '~/components/elements/UiApplicationsFormsLayout';
import {UiCard} from '~/components/elements/UiCard';
import TextModal from '~/components/modals/TextModal';
import UploadImageModal from '~/components/modals/UploadImageModal';
import DatePickerCustomInput from '~/components/shared/DatePickerCustomInput';
import StudentFormLabel from '~/components/shared/StudentFormLabel';
import {STUDENT_ENDPOINTS, TODAY_DEEPLINK} from '~/constants';
import {useBoolBag, useInput, useNotification, useRequestState} from '~/hooks';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getSchoolsDropdown} from '~/redux/schools/slice';
import {SchoolEntity} from '~/redux/schools/types';
import {addStudentRegister} from '~/redux/students/thunk';
import {StudentUser} from '~/redux/students/types';
import {formatDate, YEAR_FORMAT} from '~/utils/helpers';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const customChakraStyles: ChakraStylesConfig = {
  singleValue: (provided: any) => {
    const fontWeight = '400';
    return {...provided, fontWeight};
  },
  placeholder: (provided: any) => {
    return {...provided, fontWeight: '400'};
  },
  dropdownIndicator: (provided: any) => {
    return {...provided, background: 'surfaceLight'};
  },
};

interface Props {}

const StudentForm = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const {data: schools, paginator, loading: getSchoolsLoading} = useSelector(getSchoolsDropdown);
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  const [isShowSuccessModal, setIsShowSuccessModal] = useState(false);
  const [schoolNameInput, setSchoolNameInput] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [errorText, setErrorText] = useState('');
  const [uploadedFile, setUploadedFile] = useState<UploadResponse[]>([]);
  const {addMessage, addValidationMessage} = useNotification(dispatch);
  const {loading} = useRequestState(addStudentRegister);
  const {boolBag, toggleBoolBag} = useBoolBag({newsAndPerks: false, agree: false, hasEmailAlready: false});

  const rules = {
    firstName: {
      presence: {allowEmpty: false},
    },
    lastName: {
      presence: {allowEmpty: false},
    },
    email: {
      presence: {allowEmpty: false},
      email: {
        message: '^Invalid email address',
      },
    },
    existingEmail: function () {
      if (boolBag.hasEmailAlready) {
        return {
          presence: {allowEmpty: false},
          email: {
            message: '^Invalid email address',
          },
          equality: {
            attribute: 'email',
            message: '^Opps, please deselect this field if your existing account is the same with your student email',
            comparator: function (email: string, existingEmail: string) {
              if (!boolBag.hasEmailAlready) {
                return true;
              }
              return email !== existingEmail;
            },
          },
        };
      }
      return undefined;
    },
    schoolId: {
      presence: {allowEmpty: false},
    },
    graduate: {
      presence: {allowEmpty: false},
    },
    studentId: {
      presence: {allowEmpty: false},
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput<StudentUser>(
    {
      firstName: '',
      lastName: '',
      email: '',
      existingEmail: '',
      schoolId: undefined,
      graduate: '',
      studentId: [],
    },
    {rules},
  );

  const isAllInputsValid = boolBag.hasEmailAlready
    ? Object.values(inputs).every(i => !!i)
    : Object.values(omit(inputs, 'existingEmail')).every(i => !!i);
  const isSchoolValid = schools?.find((school: SchoolEntity) => school?.id === inputs.schoolId);
  const canSubmit =
    isSchoolValid && boolBag.agree && boolBag.newsAndPerks && inputs.studentId?.length && isAllInputsValid;

  const onSubmit = async () => {
    setErrorText('');
    if (validation.getErrors()) {
      return;
    }

    const resAction = await dispatch(addStudentRegister({data: {user: inputs}}));
    if (requestOK(resAction, addStudentRegister)) {
      setIsShowSuccessModal(true);
    } else {
      // @ts-ignore
      if (resAction?.payload?.errMsg) setErrorText(resAction?.payload?.errMsg);
    }
  };

  const handleOpenImageToNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  const onUploadSuccess = (files: UploadResponse[]) => {
    const twoImages = files?.slice(0, 2).map(file => file.accessUrl);
    setInputs({studentId: twoImages});
    setUploadedFile(files.slice(0, 2));
  };

  const handleChangeEmailValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimSpaces(event?.target?.value);
    setInputs({email: value});
  };

  const handleChangeExistingEmailValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimSpaces(event?.target?.value);
    setInputs({existingEmail: value});
  };

  const toggleEaseEmailCheckBox = () => {
    toggleBoolBag('hasEmailAlready');
    if (boolBag.hasEmailAlready) {
      setInputs({existingEmail: ''});
    }
  };

  const handleOpenDeeplink = () => {
    window.open(TODAY_DEEPLINK, '_self');
  };

  return (
    <>
      <TextModal
        isShowing={isShowSuccessModal}
        title={`Thank you for signing up! `}
        logo="student-ease-logo"
        hideCloseButton
        onClickSubmit={isWebview ? handleOpenDeeplink : undefined}
        buttonTitle="Done"
      >
        <UiText mt={5} fontSize="lg">
          Our team will verify your account and an email will be sent to you within 3 working days.
        </UiText>
      </TextModal>
      <UploadImageModal
        isShowing={isShowUploadModal}
        onDismissModal={() => setIsShowUploadModal(false)}
        onSuccessUpload={onUploadSuccess}
        multiple
        limit={2}
        endpoint={STUDENT_ENDPOINTS.UPLOAD}
      />
      <UiApplicationsFormsLayout height="100%" bg="white" flexGrow={1}>
        <Col alignItems="center" textAlign="center" flexGrow={1}>
          <UiCard variant="rounded" shadow="none" width="100%" py={2} px={{base: 4, sm: 8}}>
            <UiSimpleGrid columns={2} spacing={4} w="100%" mt={2}>
              <UiFormControl id="firstName" isRequired isInvalid={!!validation?.errors?.firstName}>
                <StudentFormLabel>First Name</StudentFormLabel>
                <UiInput
                  variant="filled"
                  value={inputs.firstName}
                  onChange={({target}) => setInputs({firstName: target.value})}
                  fontWeight="400"
                  height="42px"
                  bg="surfaceLight"
                  borderRadius={4}
                  placeholder="as per student ID"
                />
                {validation?.errors?.firstName && (
                  <UiFormErrorMessage>{validation?.errors?.firstName[0]}</UiFormErrorMessage>
                )}
              </UiFormControl>
              <UiFormControl id="lastName" isRequired isInvalid={!!validation?.errors?.lastName}>
                <StudentFormLabel>Last Name</StudentFormLabel>
                <UiInput
                  variant="filled"
                  value={inputs.lastName}
                  onChange={({target}) => setInputs({lastName: target.value})}
                  fontWeight="400"
                  height="42px"
                  bg="surfaceLight"
                  borderRadius={4}
                  placeholder="as per student ID"
                />
                {validation?.errors?.lastName && (
                  <UiFormErrorMessage>{validation?.errors?.lastName[0]}</UiFormErrorMessage>
                )}
              </UiFormControl>
            </UiSimpleGrid>
            <UiSimpleGrid columns={1} mt={2} w="100%" alignItems="start" spacing={4}>
              <UiBox display="flex" alignItems="center">
                <UiCheckbox size="md" isChecked={boolBag.hasEmailAlready} onChange={toggleEaseEmailCheckBox} mr={4} />
                <StudentFormLabel
                  fontStyle="italic"
                  _hover={{cursor: 'pointer'}}
                  onClick={toggleEaseEmailCheckBox}
                  m={0}
                  fontSize="14px"
                >
                  I have an existing Ease account
                </StudentFormLabel>
              </UiBox>
              {boolBag.hasEmailAlready && (
                <UiFormControl id="existingEmail" isRequired isInvalid={!!validation?.errors?.existingEmail}>
                  <UiInput
                    variant="filled"
                    value={inputs.existingEmail}
                    onChange={event => handleChangeExistingEmailValue(event)}
                    fontWeight="400"
                    height="42px"
                    bg="surfaceLight"
                    borderRadius={4}
                    placeholder="Enter the email of your Ease account"
                  />
                  {validation?.errors?.existingEmail && (
                    <UiFormErrorMessage>{validation?.errors?.existingEmail[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
              )}
            </UiSimpleGrid>
            <UiFormControl id="email" isRequired isInvalid={!!validation?.errors?.email} mt={5}>
              <StudentFormLabel>Student Email Address</StudentFormLabel>
              <UiInput
                variant="filled"
                value={inputs.email}
                onChange={event => handleChangeEmailValue(event)}
                fontWeight="400"
                height="42px"
                bg="surfaceLight"
                borderRadius={4}
                placeholder="Must be a valid email address"
              />
              {validation?.errors?.email && <UiFormErrorMessage>{validation?.errors?.email[0]}</UiFormErrorMessage>}
            </UiFormControl>

            <UiSimpleGrid columns={{base: 1, sm: 2}} spacingX={4} spacingY={5} w="100%" mt={5}>
              <UiFormControl id="schoolId" isRequired isInvalid={!!validation?.errors?.schoolId}>
                <StudentFormLabel>What school are you from?</StudentFormLabel>
                <SchoolsDropdown
                  onChange={value => {
                    setInputs({schoolId: Number(value?.value)});
                    setSchoolName('');
                  }}
                  selectedFontWeight="400"
                  isClearable
                  noOptionsMessage={() => null}
                  onInputChange={(inputValue: string) => {
                    setSchoolNameInput(inputValue);
                  }}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      setSchoolName(schoolNameInput);
                    }
                  }}
                  styleControl={{
                    height: '42px',
                    borderRadius: 4,
                    background: 'surfaceLight',
                  }}
                  customChakraStyles={customChakraStyles}
                  placeholder="School name"
                />

                {validation?.errors?.schoolId && (
                  <UiFormErrorMessage>{validation?.errors?.schoolId[0]}</UiFormErrorMessage>
                )}
              </UiFormControl>

              {(isSchoolValid || !schoolName) && (
                <UiFormControl id="graduate" isRequired isInvalid={!!validation?.errors?.graduate}>
                  <StudentFormLabel>Which year will you graduate?</StudentFormLabel>
                  <AppDatePicker
                    selected={
                      inputs.graduate && moment(inputs?.graduate).isValid() === true
                        ? new Date(inputs?.graduate)
                        : undefined
                    }
                    onChange={value => {
                      setInputs({graduate: formatDate(moment(value).endOf('year'))});
                    }}
                    dropdownMode="select"
                    showYearPicker
                    dateFormat={YEAR_FORMAT}
                    customInput={
                      <DatePickerCustomInput fontWeight="400" height="42px" bg="surfaceLight" borderRadius={4} />
                    }
                    minDate={moment().subtract(1, 'years').toDate()}
                    autoComplete="off"
                    placeholderText="YYYY"
                  />
                  {validation?.errors?.graduate && (
                    <UiFormErrorMessage>{validation?.errors?.graduate[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
              )}
            </UiSimpleGrid>

            {!isSchoolValid && schoolName && (
              <UiText mt={5}>
                Unfortunately, your school is not currently participating of the program. At this time you would not be
                eligible for the Students@Ease Program. However, you may send in a request to
                students@ease-healthcare.com to add your school to the list and we will be in touch with you on next
                steps
              </UiText>
            )}

            {(isSchoolValid || !schoolName) && (
              <>
                <UiFormControl id="studentId" isRequired isInvalid={!!validation?.errors?.studentId} mt={5}>
                  <StudentFormLabel>
                    Upload a photo of your student ID - Front and Back (with the date of validity)
                  </StudentFormLabel>
                  <UiBox borderWidth={1} borderColor="lightGrey" p={1} borderRadius={spacing.mini} flexWrap="nowrap">
                    <Row alignItems="center">
                      <UiButton
                        title="Choose file"
                        fontSize="xs"
                        bg="greenLight"
                        minH={spacing.smedium}
                        onClick={() => setIsShowUploadModal(true)}
                      />
                      {!inputs.studentId?.length && (
                        <UiText ml={4} fontSize="xs" color="blackSecondary">
                          No file chosen
                        </UiText>
                      )}
                    </Row>

                    {uploadedFile?.length > 0 && (
                      <Col my={3}>
                        {uploadedFile?.map(file => (
                          <UiText
                            _hover={{cursor: 'pointer'}}
                            key={file.accessUrl}
                            ml={{base: 0, sm: 2}}
                            onClick={() => handleOpenImageToNewTab(file.accessUrl)}
                          >
                            {file.fileName}
                          </UiText>
                        ))}
                      </Col>
                    )}
                  </UiBox>
                  {validation?.errors?.studentId && (
                    <UiFormErrorMessage>{validation?.errors?.studentId[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>

                <UiFormControl id="agree" isRequired mt={3}>
                  <Row alignItems="center">
                    <UiRadio
                      onClick={() => toggleBoolBag('agree')}
                      isChecked={boolBag.agree}
                      borderColor="dark"
                      size="md"
                    />
                    <StudentFormLabel
                      onClick={() => toggleBoolBag('agree')}
                      _hover={{cursor: 'pointer'}}
                      fontSize={{base: 'xs', sm: 'sm'}}
                      mt={2}
                      ml={2}
                    >
                      Sign me up for the Ease Newsletter to get exclusive student news and perks
                    </StudentFormLabel>
                  </Row>
                </UiFormControl>

                <UiFormControl id="newsAndPerks" isRequired mt={1}>
                  <Row alignItems="center">
                    <UiRadio
                      onClick={() => toggleBoolBag('newsAndPerks')}
                      isChecked={boolBag.newsAndPerks}
                      borderColor="dark"
                      size="md"
                    />
                    <StudentFormLabel
                      onClick={() => toggleBoolBag('newsAndPerks')}
                      _hover={{cursor: 'pointer'}}
                      fontSize={{base: 'xs', sm: 'sm'}}
                      mt={2}
                      ml={2}
                    >
                      By submitting this form, I agree to all program terms and conditions
                    </StudentFormLabel>
                  </Row>
                </UiFormControl>
              </>
            )}
          </UiCard>
        </Col>
        <Col alignItems="center">
          <UiButton
            title="Submit"
            w={{base: '95%', sm: '25%'}}
            fontSize="xs"
            _hover={{
              background: 'baseColor',
              color: 'white',
            }}
            bg="fadedJade"
            alignItems="center"
            justifyContent="center"
            display="flex"
            onClick={onSubmit}
            isDisabled={!canSubmit}
            mt={5}
            isLoading={loading}
          />

          {errorText && (
            <Row bg="red" p={2} w="100%" borderRadius={8} mt={4}>
              <Col mr={1}>
                <UiIcon as={HiExclamationCircle} boxSize="20px" color="white" />
              </Col>
              <Col>
                <UiText color="white" textAlign="left" fontWeight="400">
                  {errorText}
                </UiText>
              </Col>
            </Row>
          )}
        </Col>
      </UiApplicationsFormsLayout>
    </>
  );
};

export default memo(StudentForm);
